/**
 * Updates the SimpleBar instance for a given list element.
 *
 * @param {HTMLElement} list - The list element to update.
 */
function updateSimpleBar(list) {
  if (list.SimpleBar) {
    list.SimpleBar.unMount();
  }
  list.SimpleBar = new SimpleBar(list);

  const simplebarYInstancesArr = list.querySelectorAll(
      '.simplebar-track.simplebar-vertical'
  );
  const simplebarXInstancesArr = list.querySelectorAll(
      '.simplebar-track.simplebar-horizontal'
  );

  if (simplebarYInstancesArr.length) {
    simplebarYInstancesArr.forEach((element, idx) => {
      if (idx > 0) {
        element.remove();
      }
    });
  }

  if (simplebarXInstancesArr.length) {
    simplebarXInstancesArr.forEach((element, idx) => {
      if (idx > 0) {
        element.remove();
      }
    });
  }
}

/**
 * Resets the focus state for all list items inside the wrapper element.
 *
 * @param {HTMLElement} wrapperElement - The wrapper element containing the select list.
 */
window.resetMetaSelectFocus = function resetMetaSelectFocus(wrapperElement) {
  const listContent = wrapperElement.querySelector('.meta-select-list-content');
  if (listContent) {
    listContent
        .querySelectorAll('.meta-select-list__item')
        .forEach((el) => el.classList.remove('is-focused'));
  } else {
    document
        .querySelectorAll('.meta-select-list__item')
        .forEach((el) => el.classList.remove('is-focused'));
  }
};

/**
 * Initializes  meta select elements and sets up the list dropdown functionality.
 *
 * @param {string} elementsArr - The CSS selector to find the select elements.
 */
window.initMetaSelect = function initMetaSelect(elementsArr) {
  const metaSelectArr = document.querySelectorAll(elementsArr);

  metaSelectArr.forEach((selectElement, idx) => {
    const parentContainer = selectElement.closest('.meta-select');
    parentContainer.style.zIndex = metaSelectArr.length - idx + 5;

    if (!parentContainer) return;

    if (!selectElement.classList.contains('is-init')) {
      selectElement.classList.add('is-init');

      const optionsLength = selectElement.querySelectorAll('option').length;
      if (optionsLength === 0) {
        const newOption = document.createElement('option');
        newOption.innerHTML = 'Selected...';
        newOption.value = '';
        selectElement.appendChild(newOption);
      }

      const disabled = selectElement.hasAttribute('disabled');

      const label = parentContainer.querySelector('label');

      const wrapper = document.createElement('div');
      wrapper.className = 'meta-select-inner';

      const placeholder = document.createElement('div');
      placeholder.className = 'meta-select-placeholder';

      const list = document.createElement('div');
      list.className = 'meta-select-list';

      const additionalListClasses = selectElement.getAttribute('data-meta-select-list-class');
      if (additionalListClasses) {
        additionalListClasses.split(' ').forEach(cls => {
          if (cls.trim()) {
            list.classList.add(cls.trim());
          }
        });
      }

      const listContent = document.createElement('div');
      listContent.className = 'meta-select-list-content';

      const isSearch = selectElement.hasAttribute('data-meta-select-search');
      let listInput = null;

      if (isSearch) {
        listInput = document.createElement('input');
        listInput.className = 'meta-select-search';
        listInput.type = 'text';
        list.appendChild(listInput);

        list.classList.add('is-search-input');
      }

      list.appendChild(listContent);

      const notInlinePosition = selectElement.hasAttribute(
          'data-meta-select-not-inline'
      );

      if (disabled) {
        parentContainer.classList.add('is-disabled-select');
      }

      /**
       * Positions the dropdown list relative to the select element.
       */
      function positionList() {
        if (!notInlinePosition) return;

        requestAnimationFrame(() => {
          if (list.parentElement !== document.body) {
            document.body.appendChild(list);
          }

          if (selectElement.closest('#popup')) {
            list.style.position = 'fixed';
          } else {
            list.style.position = 'absolute';
          }

          list.style.position = 'fixed';
          list.style.minWidth = `${wrapper.clientWidth}px`;
          list.style.maxWidth = `${wrapper.clientWidth}px`;

          list.style.visibility = 'hidden';
          list.style.display = 'block';
          list.style.top = '0';
          list.style.left = '0';

          const listHeight = list.offsetHeight;

          const selectRect = wrapper.getBoundingClientRect();

          const bottomSpace = window.innerHeight - selectRect.bottom;
          const topSpace = selectRect.top;

          let top;

          if (bottomSpace >= listHeight + 3) {
            top = selectRect.bottom + 3;
          } else if (topSpace >= listHeight + 3) {
            top = selectRect.top - listHeight - 3;
          } else {
            top = selectRect.bottom + 3;
            list.style.maxHeight = `${bottomSpace - 10}px`;
            list.style.overflowY = 'auto';
          }

          const left = selectRect.left;

          list.style.top = `${top}px`;
          list.style.left = `${left}px`;

          list.style.visibility = 'visible';
        });
      }

      /**
       * Updates the dropdown list with the current options from the select element.
       */
      function updateList() {
        listContent.innerHTML = '';
        placeholder.textContent =
            selectElement.options[selectElement.selectedIndex]?.text ||
            'Selected...';

        Array.from(selectElement.options).forEach((option) => {
          const item = document.createElement('div');
          item.className = 'meta-select-list__item';
          item.textContent = option.text;
          item.dataset.value = option.value;

          if (option.selected) {
            item.classList.add('is-selected');
          }

          if (option.disabled) {
            item.classList.add('is-disabled');
          }

          if (option.classList.contains('is-placeholder')) {
            item.classList.add('is-placeholder');
          }

          item.addEventListener('click', () => {
            if (!item.classList.contains('is-disabled')) {
              setMetaSelectValue(selectElement.id, option.value);
              closeList();
            }
          });

          item.addEventListener('mouseenter', () => {
            resetFocus();
          });

          listContent.appendChild(item);
        });

        updateSimpleBar(list);
      }

      let currentFocusIndex = -1;

      /**
       * Updates the visual focus state of list items based on the current focus index.
       *
       * @param {HTMLElement[]} visibleItems - Array of visible and focusable list items.
       */
      function updateFocusItem(visibleItems) {
        visibleItems.forEach((item, i) => {
          if (i === currentFocusIndex) {
            item.classList.add('is-focused');
          } else {
            item.classList.remove('is-focused');
          }
        });
      }

      /**
       * Resets the current focus index and removes focus classes from all list items.
       */
      function resetFocus() {
        currentFocusIndex = -1;
        listContent
            .querySelectorAll('.meta-select-list__item')
            .forEach((el) => el.classList.remove('is-focused'));
      }

      /**
       * Handles keyboard navigation for the dropdown list.
       * Supports ArrowUp, ArrowDown, and Enter keys to navigate and select options.
       *
       * @param {KeyboardEvent} e - The keyboard event object.
       */
      function handleKeydown(e) {
        const visibleItems = Array.from(
            listContent.querySelectorAll('.meta-select-list__item')
        ).filter((item) => {
          return (
              item.style.display !== 'none' &&
              !item.classList.contains('is-disabled')
          );
        });

        if (!visibleItems.length) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % visibleItems.length;
          updateFocusItem(visibleItems);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          currentFocusIndex =
              (currentFocusIndex - 1 + visibleItems.length) % visibleItems.length;
          updateFocusItem(visibleItems);
        } else if (e.key === 'Enter' && currentFocusIndex >= 0) {
          e.preventDefault();
          visibleItems[currentFocusIndex].click();
        }

        const scrollEl = listContent.closest('.simplebar-content-wrapper');
        const focusedEl = visibleItems[currentFocusIndex];

        if (scrollEl && focusedEl) {
          const scrollTop = scrollEl.scrollTop;
          const scrollHeight = scrollEl.clientHeight;

          const elTop = focusedEl.offsetTop;
          const elBottom = elTop + focusedEl.offsetHeight;

          if (currentFocusIndex === 0) {
            scrollEl.scrollTop = 0;
          } else if (elBottom > scrollTop + scrollHeight) {
            scrollEl.scrollTop = elBottom - scrollHeight + 8;
          } else if (elTop < scrollTop) {
            scrollEl.scrollTop = elTop - 8;
          }
        }
      }

      /**
       * Opens the dropdown list and positions it correctly.
       */
      function openList() {
        const innersGlobalArr = document.querySelectorAll('.meta-select-inner');
        const listsGlobalArr = document.querySelectorAll('.meta-select-list');

        for (let i = 0; i < innersGlobalArr.length; i++) {
          innersGlobalArr[i].classList.remove('is-open');
          if (listsGlobalArr[i]) {
            listsGlobalArr[i]?.classList.remove('is-open');
          }
          resetMetaSelectFocus(innersGlobalArr[i]);
        }

        list.classList.add('is-open');
        wrapper.classList.add('is-open');

        positionList();

        window.addEventListener('resize', positionList);
        updateSimpleBar(list);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            positionList();
          });
        });

        if (isSearch) {
          listInput.focus();
        }

        if (notInlinePosition) {
          bindGlobalScrollClose();
        }

        document.addEventListener('keydown', handleKeydown);
      }

      /**
       * Closes the dropdown list and removes event listeners.
       */
      function closeList() {
        list.classList.remove('is-open');
        wrapper.classList.remove('is-open');
        window.removeEventListener('resize', positionList);

        if (notInlinePosition) {
          wrapper.appendChild(list);
        }

        if (notInlinePosition) {
          unbindGlobalScrollClose();
        }

        resetFocus();
        document.removeEventListener('keydown', handleKeydown);
      }

      /**
       * Binds scroll and touchmove events on the window to close the dropdown list.
       */
      function bindGlobalScrollClose() {
        window.addEventListener('scroll', closeList);
        window.addEventListener('touchmove', closeList);
      }

      /**
       * Unbinds scroll and touchmove events from the window that close the dropdown list.
       */
      function unbindGlobalScrollClose() {
        window.removeEventListener('scroll', closeList);
        window.removeEventListener('touchmove', closeList);
      }

      /**
       * Toggles the visibility of the dropdown list.
       */
      const toggleList = () => {
        if (list.classList.contains('is-open')) {
          closeList();
        } else {
          openList();
        }
      };

      if (!disabled) {
        wrapper.addEventListener('click', (ev) => {
          if (
              ev.target !== list &&
              ev.target !== label &&
              ev.target !== listInput
          ) {
            toggleList();
          }
        });

        label?.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          toggleList();
        });
      }

      document.addEventListener('click', (e) => {
        if (
            !wrapper.contains(e.target) &&
            !list.contains(e.target) &&
            (!label || !label.contains(e.target))
        ) {
          closeList();
        }
      });

      /**
       * Shows or hides related input fields based on the selected option's data attributes.
       */
      const updateVisibility = () => {
        const inputIds = new Set();

        Array.from(selectElement.options).forEach((option) => {
          const ids = option.dataset.msShowFields?.split(',') || [];
          ids.forEach((id) => inputIds.add(id.trim()));
        });

        const inputs = Array.from(inputIds)
            .map((id) => document.getElementById(id))
            .filter(Boolean);

        const selectedOption =
            selectElement.options[selectElement.selectedIndex];
        const toShow =
            selectedOption.dataset.msShowFields
                ?.split(',')
                .map((id) => id.trim()) || [];

        inputs.forEach((input) => {
          const parentMetaInput = input.closest('.meta-input');
          const parentMetaSelect = input.closest('.meta-select');
          const parentMetaTimePicker = input.closest('.meta-time-picker');
          const parentMetaCheckbox = input.closest('.meta-checkbox');
          const parentMetaSwitch = input.closest('.meta-switch');

          if (toShow.includes(input.id)) {
            parentMetaInput?.classList.remove('hidden');
            parentMetaSelect?.classList.remove('hidden');
            parentMetaTimePicker?.classList.remove('hidden');
            parentMetaCheckbox?.classList.remove('hidden');
            parentMetaSwitch?.classList.remove('hidden');
          } else {
            parentMetaInput?.classList.add('hidden');
            parentMetaSelect?.classList.add('hidden');
            parentMetaTimePicker?.classList.add('hidden');
            parentMetaCheckbox?.classList.add('hidden');
            parentMetaSwitch?.classList.add('hidden');
          }
        });
      };

      selectElement.addEventListener('change', function () {
        updateList();
        updateVisibility();
      });

      wrapper.appendChild(placeholder);
      wrapper.appendChild(list);
      parentContainer.appendChild(wrapper);
      selectElement.style.display = 'none';

      updateList();
      updateVisibility();

      /**
       * Calculates the Levenshtein distance between two strings.
       *
       * @param {string} a - First string.
       * @param {string} b - Second string.
       * @returns {number} The Levenshtein distance between the two strings.
       */
      function getLevenshteinDistance(a, b) {
        const matrix = [];

        const lenA = a.length;
        const lenB = b.length;

        for (let i = 0; i <= lenB; i++) {
          matrix[i] = [i];
        }
        for (let j = 0; j <= lenA; j++) {
          matrix[0][j] = j;
        }

        for (let i = 1; i <= lenB; i++) {
          for (let j = 1; j <= lenA; j++) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
              matrix[i][j] = matrix[i - 1][j - 1];
            } else {
              matrix[i][j] = Math.min(
                  matrix[i - 1][j - 1] + 1,
                  matrix[i][j - 1] + 1,
                  matrix[i - 1][j] + 1
              );
            }
          }
        }

        return matrix[lenB][lenA];
      }

      if (isSearch) {
        listInput.addEventListener('input', () => {
          const searchTerm = listInput.value.toLowerCase();
          const items = listContent.querySelectorAll('.meta-select-list__item');
          let visibleCount = 0;

          items.forEach((item) => {
            const text = item.textContent.toLowerCase();
            const distance = getLevenshteinDistance(text, searchTerm);

            if (
                searchTerm === '' ||
                text.includes(searchTerm) ||
                distance <= 1
            ) {
              item.style.display = '';
              visibleCount++;
            } else {
              item.style.display = 'none';
            }
          });

          let noResultsSpan = listContent.querySelector(
              '.meta-select-list__item-no-results'
          );

          if (visibleCount === 0) {
            if (!noResultsSpan) {
              noResultsSpan = document.createElement('span');
              noResultsSpan.className = 'meta-select-list__item-no-results';
              noResultsSpan.textContent = 'No results';
              listContent.appendChild(noResultsSpan);
            }
          } else {
            if (noResultsSpan) {
              noResultsSpan.remove();
            }
          }

          resetFocus();
        });

        const isNova = selectElement.hasAttribute('data-meta-select-nova');

        if (isNova) {
          let typingTimer;
          const doneTypingInterval = 500;
          let prevValue = '';

          /**
           * Handles the logic after the user has stopped typing in the search input.
           * Used for Nova search integration.
           */
          function doneTyping() {
            const novaSelectSearchValue = listInput.value;
            if (prevValue !== novaSelectSearchValue) {
              prevValue = novaSelectSearchValue;
              startLoaderSelect();
              /*load(
                'page/loadWarehousesKeywords',
                'city_id#nova_city',
                `keywords=${novaSelectSearchValue}`
              );*/
            }
          }

          listInput.addEventListener('input', function (event) {
            clearTimeout(typingTimer);
            typingTimer = setTimeout(doneTyping, doneTypingInterval);
          });
        }
      }
    }
  });
};

/**
 * Sets the value of a select element and triggers a change event.
 *
 * @param {string} selectId - The ID of the select element.
 * @param {string} value - The value to set for the select element.
 */
window.setMetaSelectValue = function setMetaSelectValue(selectId, value) {
  const selectElement = document.getElementById(selectId);
  if (!selectElement) return;

  const options = selectElement.options;
  if (!options) return;

  const optionExists = Array.from(options).some(
      (option) => option.value === value
  );
  if (!optionExists) return;

  selectElement.value = value;
  selectElement.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * Updates the dropdown list of a specific select element.
 *
 * @param {string} selectId - The ID of the select element to update.
 */
window.updateMetaSelect = function updateMetaSelect(selectId) {
  const selectElement = document.getElementById(selectId);
  if (!selectElement) return;

  const parentContainer = selectElement.closest('.meta-select');
  if (!parentContainer) return;

  const wrapper = parentContainer.querySelector('.meta-select-inner');
  const placeholder = wrapper.querySelector('.meta-select-placeholder');
  const list = wrapper.querySelector('.meta-select-list');
  const listContent = wrapper.querySelector('.meta-select-list-content');

  const disabled = selectElement.hasAttribute('disabled');

  if (disabled) {
    parentContainer.classList.add('is-disabled-select');
  }

  if (!list) return;

  listContent.innerHTML = '';
  Array.from(selectElement.options).forEach((option) => {
    const item = document.createElement('div');
    item.className = 'meta-select-list__item';
    item.textContent = option.text;
    item.dataset.value = option.value;

    if (option.selected) {
      item.classList.add('is-selected');
    }

    if (option.disabled) {
      item.classList.add('is-disabled');
    }

    if (option.classList.contains('is-placeholder')) {
      item.classList.add('is-placeholder');
    }

    item.addEventListener('click', () => {
      if (!item.classList.contains('is-disabled')) {
        setMetaSelectValue(selectId, option.value);
        list.classList.remove('is-open');
        wrapper.classList.remove('is-open');
      }
    });

    item.addEventListener('mouseenter', () => {
      resetMetaSelectFocus(wrapper);
    });

    listContent.appendChild(item);
  });

  placeholder.textContent =
      selectElement.options[selectElement.selectedIndex]?.text || 'Selected...';

  updateSimpleBar(list);

  resetMetaSelectFocus(wrapper);
};

/**
 * Updates the dropdown list of all select elements on the page.
 */
window.updateAllMetaSelects = function updateAllMetaSelects() {
  document.querySelectorAll('[data-meta-select]').forEach((selectElement) => {
    const parentContainer = selectElement.closest('.meta-select');
    if (!parentContainer) return;

    const wrapper = parentContainer.querySelector('.meta-select-inner');
    const placeholder = wrapper.querySelector('.meta-select-placeholder');
    const list = wrapper.querySelector('.meta-select-list');
    const listContent = wrapper.querySelector('.meta-select-list-content');

    const disabled = selectElement.hasAttribute('disabled');

    if (disabled) {
      parentContainer.classList.add('is-disabled-select');
    }

    if (!list) return;

    listContent.innerHTML = '';
    Array.from(selectElement.options).forEach((option) => {
      const item = document.createElement('div');
      item.className = 'meta-select-list__item';
      item.textContent = option.text;
      item.dataset.value = option.value;

      if (option.selected) {
        item.classList.add('is-selected');
      }

      item.addEventListener('click', () => {
        setMetaSelectValue(selectElement.id, option.value);
        list.classList.remove('is-open');
        wrapper.classList.remove('is-open');
      });

      item.addEventListener('mouseenter', () => {
        resetMetaSelectFocus(wrapper);
      });

      listContent.appendChild(item);
    });

    placeholder.textContent =
        selectElement.options[selectElement.selectedIndex]?.text || 'Selected...';

    updateSimpleBar(list);

    resetMetaSelectFocus(wrapper);
  });
};

/**
 * A utility to handle global "wheel" event listeners that trigger a callback
 * when the user scrolls the mouse wheel **outside** a given element.
 */
const OutsideWheelListener = (() => {
  // Stores references to each list and its associated event handler
  const listeners = new WeakMap();

  return {
    /**
     * Adds a global "wheel" event listener that triggers the callback
     * if the wheel scroll happens outside the given element.
     *
     * @param {Element} list - The DOM element to monitor for outside scroll.
     * @param {Function} callback - The function to call when the scroll happens outside the element.
     */
    add(list, callback) {
      if (!list || typeof callback !== 'function') return;

      const handler = (event) => {
        const hovered = document.elementFromPoint(event.clientX, event.clientY);
        if (!list.contains(hovered)) {
          callback();
        }
      };

      document.addEventListener('wheel', handler, { passive: true });
      listeners.set(list, handler);
    },

    /**
     * Removes the previously added "wheel" listener for the given element.
     *
     * @param {Element} list - The DOM element associated with the listener to remove.
     */
    remove(list) {
      const handler = listeners.get(list);
      if (handler) {
        document.removeEventListener('wheel', handler);
        listeners.delete(list);
      }
    },
  };
})();

initMetaSelect('[data-meta-select]');